import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import ServiceStatusMonitor from '../components/ServiceStatusMonitor';
import { optimizedApiService } from '../lib/apiOptimized';
import { errorHandler } from '../lib/errorHandler';
import { Upload, FileText, Download, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

// Type definitions
interface BulkJob {
  id: number;
  name: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'partial';
  total_labels: number;
  successful_labels: number;
  failed_labels: number;
  created_at: string;
  completed_at?: string;
  error_log?: string;
  output_file?: string;
}

interface BulkJobsResponse {
  count: number;
  results: BulkJob[];
}

const BulkUpload: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showRates, setShowRates] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  
  // Service type is now read from CSV, not UI dropdown
  const defaultServiceType = '03'; // Ground (fallback only if CSV doesn't specify)

  // Preview mutation - shows CSV data before processing
  const previewMutation = useMutation({
    mutationFn: ({ file, serviceType }: { file: File; serviceType: string }) =>
      optimizedApiService.bulkJobs.previewBulkFile(file, serviceType),
    onSuccess: (response: any) => {
      setPreviewData(response.preview_data || []);
      setShowRates(response.show_rates || false);
      setTotalRows(response.total_rows || 0);
      setShowPreview(true);
      
      if (response.show_rates) {
        toast.success(`✅ Parsed ${response.total_rows} shipments successfully! Rates are included.`);
      } else {
        toast.success(`✅ Parsed ${response.total_rows} shipments successfully!`, {
          duration: 4000,
        });
        if (!response.profile_exists) {
          toast('Rate display is disabled. Contact administrator to enable rate visibility.', {
            duration: 6000,
            icon: 'ℹ️',
          });
        } else {
          toast('Rate display is disabled in your profile settings.', {
            duration: 6000,
            icon: 'ℹ️',
          });
        }
      }
    },
    onError: (error: any) => {
      errorHandler.handleApiError(error, 'CSV Preview');
      toast.error(error.response?.data?.error || 'Failed to preview file');
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ file, serviceType }: { file: File; serviceType: string }) =>
      optimizedApiService.bulkJobs.uploadBulkFile(file, serviceType),
    onSuccess: (response) => {
      toast.success('File uploaded successfully! Processing labels...');
      // Invalidate all related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['bulk-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['labels'] });
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
      // Reset state
      setSelectedFile(null);
      setShowPreview(false);
      setPreviewData([]);
    },
    onError: (error: any) => {
      errorHandler.handleApiError(error, 'Bulk Upload');
      toast.error(error.response?.data?.error || 'Failed to upload file');
    },
  });

  // Query for bulk jobs with automatic polling for processing jobs
  const { data: bulkJobsData, refetch: refetchBulkJobs } = useQuery<BulkJobsResponse>({
    queryKey: ['bulk-jobs'],
    queryFn: () => optimizedApiService.bulkJobs.getBulkJobs({ page_size: 10 }) as Promise<BulkJobsResponse>,
    refetchInterval: (query) => {
      // Poll every 3 seconds if there are any processing jobs
      const data = query.state.data as BulkJobsResponse | undefined;
      const hasProcessingJobs = data?.results?.some((job: BulkJob) => job.status === 'processing');
      return hasProcessingJobs ? 3000 : false;
    },
    refetchIntervalInBackground: true,
  });

  // Auto-refresh shipments and labels when bulk jobs complete
  useEffect(() => {
    const data = bulkJobsData as BulkJobsResponse | undefined;
    if (data?.results) {
      const completedJobs = data.results.filter((job: BulkJob) => 
        job.status === 'completed' || job.status === 'partial' || job.status === 'failed'
      );
      
      if (completedJobs.length > 0) {
        // Refresh related data when jobs complete
        queryClient.invalidateQueries({ queryKey: ['shipments'] });
        queryClient.invalidateQueries({ queryKey: ['labels'] });
        queryClient.invalidateQueries({ queryKey: ['packages'] });
        queryClient.invalidateQueries({ queryKey: ['addresses'] });
      }
    }
  }, [bulkJobsData, queryClient]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Reset preview when new file is selected
      setShowPreview(false);
      setPreviewData([]);
    }
  };

  const handlePreview = () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    previewMutation.mutate({ file: selectedFile, serviceType: defaultServiceType });
  };

  const handleConfirmUpload = () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    uploadMutation.mutate({ file: selectedFile, serviceType: defaultServiceType });
  };

  const handleCancelPreview = () => {
    setShowPreview(false);
    setPreviewData([]);
    setSelectedFile(null);
  };

  const downloadResultsMutation = useMutation({
    mutationFn: (jobId: number) => optimizedApiService.bulkJobs.downloadBulkResults(jobId),
    onSuccess: () => {
      toast.success('Results downloaded successfully!');
    },
    onError: (error: any) => {
      errorHandler.handleApiError(error, 'Bulk Download Results');
      toast.error(error.response?.data?.error || 'Failed to download results');
    },
  });

  const downloadSampleFile = () => {
    const sampleData = `to_name,to_address1,to_address2,to_city,to_state,to_postal,to_country,to_phone,to_company,length,width,height,weight,description,service_type,reference_number,reference_number_2,to_residential
John Smith,123 Main Street,Apt 4B,Atlanta,GA,30301,US,5551234567,Smith Corp,12,10,8,2.5,Electronics Package,03,REF001,CUST001,1
Jane Doe,456 Oak Avenue,,New York,NY,10001,US,5559876543,Doe Industries,10,8,6,1.8,Clothing Package,03,REF002,CUST002,0
Bob Johnson,789 Pine Street,Suite 200,Los Angeles,CA,90210,US,5554567890,Johnson LLC,15,12,10,3.2,Books Package,03,REF003,CUST003,0
Sarah Wilson,321 Elm Drive,,Chicago,IL,60601,US,5553219876,Wilson Co,8,6,4,1.2,Jewelry Package,03,REF004,CUST004,1
Mike Brown,654 Maple Lane,Unit 5,Houston,TX,77001,US,5556543210,Brown Enterprises,14,11,9,2.8,Tools Package,03,REF005,CUST005,1`;

    const blob = new Blob([sampleData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_bulk_upload.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Sample file downloaded successfully!');
  };

  const serviceTypes = [
    { value: '01', label: 'Next Day Air' },
    { value: '02', label: '2nd Day Air' },
    { value: '03', label: 'Ground' },
    { value: '12', label: '3 Day Select' },
    { value: '13', label: 'Next Day Air Saver' },
    { value: '14', label: 'Next Day Air Early A.M.' },
    { value: '15', label: 'Express Plus' },
    { value: '17', label: 'Worldwide Express' },
    { value: '18', label: 'Worldwide Expedited' },
    { value: '19', label: 'Worldwide Express Plus' },
  ];

  const getStatusIcon = (status: BulkJob['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-success-600" />;
      case 'processing':
        return <Clock className="h-5 w-5 text-warning-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-error-600" />;
      default:
        return <Clock className="h-5 w-5 text-secondary-600" />;
    }
  };

  const getStatusBadgeVariant = (status: BulkJob['status']) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  return (
    <div className="space-y-8">
      {/* Service Status Monitor */}
      <ServiceStatusMonitor />

      <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">Bulk Label Upload</h1>
        <p className="text-secondary-600">
          Upload an Excel or CSV file to create multiple shipping labels at once.
        </p>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Upload className="h-5 w-5" />
            <span>Upload File</span>
          </CardTitle>
          <CardDescription>
            Select an Excel (.xlsx) or CSV file with shipping data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* File Upload */}
          <div>
            <label className="label">Select File *</label>
            <div className="mt-2">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="block w-full text-sm text-secondary-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              />
            </div>
            {selectedFile && (
              <div className="mt-2 flex items-center space-x-2">
                <FileText className="h-4 w-4 text-secondary-600" />
                <span className="text-sm text-secondary-700">{selectedFile.name}</span>
                <Badge variant="default">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Badge>
              </div>
            )}
          </div>

          {/* CSV Instructions - Service Type and Residential */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">📋 Important CSV Fields:</h4>
            <div className="space-y-2 text-sm text-blue-800">
              <div>
                <strong>service_type</strong> - Specify UPS service for each row:
                <ul className="ml-4 mt-1 text-xs">
                  <li>• 01 = Next Day Air</li>
                  <li>• 02 = 2nd Day Air</li>
                  <li>• 03 = Ground (default)</li>
                  <li>• 12 = 3 Day Select</li>
                  <li>• 13 = Next Day Air Saver</li>
                  <li>• 14 = Next Day Air Early A.M.</li>
                </ul>
              </div>
              <div>
                <strong>to_residential</strong> - Residential address indicator:
                <ul className="ml-4 mt-1 text-xs">
                  <li>• 1 = Residential address</li>
                  <li>• 0 or blank = Commercial address</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {!showPreview ? (
            <div className="flex justify-between items-center">
              <Button
                onClick={downloadSampleFile}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Download Sample CSV</span>
              </Button>
              <Button
                onClick={handlePreview}
                loading={previewMutation.isPending}
                disabled={!selectedFile || previewMutation.isPending}
                variant="primary"
                className="flex items-center space-x-2"
              >
                <FileText className="h-4 w-4" />
                <span>{previewMutation.isPending ? 'Parsing CSV...' : 'Preview CSV'}</span>
              </Button>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <Button
                onClick={handleCancelPreview}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <XCircle className="h-4 w-4" />
                <span>Cancel</span>
              </Button>
              <Button
                onClick={handleConfirmUpload}
                loading={uploadMutation.isPending}
                disabled={uploadMutation.isPending}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4" />
                <span>{uploadMutation.isPending ? 'Creating Labels...' : `Confirm & Create ${totalRows} Labels`}</span>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Table */}
      {showPreview && previewData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>CSV Preview - {totalRows} Shipments</span>
            </CardTitle>
            <CardDescription>
              Review the parsed data before creating labels. 
              {showRates ? (
                ' Rates are calculated based on your pricing settings.'
              ) : (
                <span className="text-warning-600"> Rate display is disabled. Contact administrator to enable rate visibility.</span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="border border-secondary-200 px-3 py-2 text-left text-xs font-semibold text-secondary-700">#</th>
                    <th className="border border-secondary-200 px-3 py-2 text-left text-xs font-semibold text-secondary-700">Recipient</th>
                    <th className="border border-secondary-200 px-3 py-2 text-left text-xs font-semibold text-secondary-700">Address</th>
                    <th className="border border-secondary-200 px-3 py-2 text-left text-xs font-semibold text-secondary-700">Service</th>
                    <th className="border border-secondary-200 px-3 py-2 text-left text-xs font-semibold text-secondary-700">Weight</th>
                    <th className="border border-secondary-200 px-3 py-2 text-left text-xs font-semibold text-secondary-700">Dimensions</th>
                    <th className="border border-secondary-200 px-3 py-2 text-left text-xs font-semibold text-secondary-700">Type</th>
                    {showRates && (
                      <th className="border border-secondary-200 px-3 py-2 text-right text-xs font-semibold text-secondary-700">Rate</th>
                    )}
                    <th className="border border-secondary-200 px-3 py-2 text-left text-xs font-semibold text-secondary-700">Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-secondary-50">
                      <td className="border border-secondary-200 px-3 py-2 text-sm">{row.row_number}</td>
                      <td className="border border-secondary-200 px-3 py-2 text-sm">
                        <div className="font-medium">{row.to_name}</div>
                        {row.to_company && <div className="text-xs text-secondary-600">{row.to_company}</div>}
                      </td>
                      <td className="border border-secondary-200 px-3 py-2 text-xs text-secondary-600">{row.to_address}</td>
                      <td className="border border-secondary-200 px-3 py-2 text-sm">
                        <Badge variant={row.service_type === '03' ? 'default' : 'secondary'}>
                          {row.service_type}
                        </Badge>
                      </td>
                      <td className="border border-secondary-200 px-3 py-2 text-sm">{row.weight}</td>
                      <td className="border border-secondary-200 px-3 py-2 text-sm">{row.dimensions}</td>
                      <td className="border border-secondary-200 px-3 py-2 text-sm">
                        {row.residential ? (
                          <Badge variant="default">Residential</Badge>
                        ) : (
                          <Badge variant="outline">Commercial</Badge>
                        )}
                      </td>
                      {showRates && (
                        <td className="border border-secondary-200 px-3 py-2 text-sm text-right font-semibold">
                          {row.calculated_rate ? (
                            <span className="text-green-600">${row.calculated_rate.toFixed(2)}</span>
                          ) : (
                            <span className="text-secondary-400">-</span>
                          )}
                        </td>
                      )}
                      <td className="border border-secondary-200 px-3 py-2 text-xs text-secondary-600">
                        {row.reference_number || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Summary */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-blue-900">Total Shipments: {totalRows}</p>
                  {showRates && (
                    <p className="text-sm text-blue-700 mt-1">
                      Total Estimated Cost: ${previewData.reduce((sum, row) => sum + (row.calculated_rate || 0), 0).toFixed(2)}
                    </p>
                  )}
                </div>
                <CheckCircle className="h-8 w-8 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* File Format Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>File Format Requirements</CardTitle>
          <CardDescription>
            Your Excel or CSV file should contain the following columns. 
            <span className="font-medium text-primary-600">Download the sample CSV file above to see the exact format.</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-secondary-900 mb-2">Required Columns:</h4>
              <ul className="text-sm text-secondary-600 space-y-1">
                <li>• to_name (Recipient Name)</li>
                <li>• to_address1 (Address Line 1)</li>
                <li>• to_city (City)</li>
                <li>• to_state (State)</li>
                <li>• to_postal (ZIP Code)</li>
                <li>• length (Package Length in inches)</li>
                <li>• width (Package Width in inches)</li>
                <li>• height (Package Height in inches)</li>
                <li>• weight (Package Weight in lbs)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-secondary-900 mb-2">Optional Columns:</h4>
              <ul className="text-sm text-secondary-600 space-y-1">
                <li>• to_company (Company Name)</li>
                <li>• to_address2 (Address Line 2)</li>
                <li>• to_country (Country - defaults to US)</li>
                <li>• to_phone (Phone Number)</li>
                <li>• description (Package Description)</li>
                <li>• service_type (01=Next Day, 02=2nd Day, 03=Ground, etc.)</li>
                <li>• reference_number (Your Reference Number)</li>
                <li>• to_residential (1=Residential, 0 or blank=Commercial)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Jobs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Bulk Jobs</CardTitle>
              <CardDescription>
                Track the status of your recent bulk uploads
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchBulkJobs()}
              className="flex items-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {(() => {
            const data = bulkJobsData as BulkJobsResponse | undefined;
            return data?.results && data.results.length > 0 ? (
              <div className="space-y-4">
                {data.results.map((job: BulkJob) => (
                <div key={job.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(job.status)}
                      <div>
                        <h4 className="font-medium text-gray-900">{job.name}</h4>
                        <p className="text-sm text-gray-600">
                          {job.total_labels} labels • {job.successful_labels} successful • {job.failed_labels} failed
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant={getStatusBadgeVariant(job.status)}>
                        {job.status}
                      </Badge>
                      {job.status === 'completed' && job.output_file && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadResultsMutation.mutate(job.id)}
                          loading={downloadResultsMutation.isPending}
                          className="flex items-center space-x-1"
                        >
                          <Download className="h-3 w-3" />
                          <span>Download</span>
                        </Button>
                      )}
                    </div>
                  </div>
                  {job.error_log && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-800 font-medium">Errors:</p>
                      <pre className="text-xs text-red-700 mt-1 whitespace-pre-wrap">{job.error_log}</pre>
                    </div>
                  )}
                  <div className="mt-2 text-xs text-gray-500">
                    Created: {new Date(job.created_at).toLocaleString()}
                    {job.completed_at && (
                      <span className="ml-4">
                        Completed: {new Date(job.completed_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-secondary-600">No bulk jobs found</p>
                <p className="text-sm text-secondary-500 mt-1">
                  Upload a file to see your bulk processing jobs here
                </p>
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkUpload;
